import { useState } from 'react';
import { IconPlus } from '@tabler/icons-react';
import { DockPanel, useDock } from '@hana/shell';
import { Button, useToast } from '@hana/ui';
import { Notis, Sec, type DockNoti } from './crmDock';
import s from './crmDock.module.css';
import { useT } from '@hana/i18n';
import './i18n';

// Bảng phải của Mint-SalesToday (khổ 1920 mở sẵn): Sổ tay · Pipeline · Thông báo. Dữ liệu là mẫu của board (P3, NT0, notis).
type K = 'note' | 'pipe' | 'noti';
const PIPE: Array<[string, [number, number, number]]> = [['DAVID', [2, 0, 2]], ['TUANNT', [0, 1, 0]], ['ANDG', [0, 0, 1]], ['AnNPH', [0, 1, 0]]];
const NOTE0: Array<[string, boolean]> = [['Gửi lịch tàu YML tuần 44 cho GREENBUD', false], ['Hỏi ANDG về giá RF Jeddah', false], ['Chuẩn bị số liệu họp Sales thứ Hai', true]];
const DOT = ['var(--d-gray)', 'var(--d-amber)', 'var(--d-blue)'];

export function useSalesTodayDock() {
  return useDock<K>('note');
}

export function SalesTodayDock({ dock }: { dock: ReturnType<typeof useSalesTodayDock> }) {
  const t = useT();
  const toast = useToast();
  const [notes, setNotes] = useState(NOTE0);
  const [draft, setDraft] = useState('');
  if (!dock.open) return null;
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    setNotes([[v, false], ...notes]);
    setDraft('');
  };
  const notis: DockNoti[] = [
    { text: 'Pricing trả giá PR-261021-094620-K8 · THACO', when: '10 phút trước', tone: 'blue', unread: true, go: () => toast({ msg: 'Mở: Pricing trả giá PR-261021-094620-K8 · THACO' }) },
    { text: 'GREENBUD mở mail báo giá Q26FRSGNYOK198', when: '35 phút trước', tone: 'blue', unread: true, go: () => toast({ msg: 'Mở: GREENBUD mở mail báo giá Q26FRSGNYOK198' }) },
    { text: 'Báo giá Q26FRSGNNSA201 còn 6 ngày hiệu lực', when: 'Hôm qua 17:00', tone: 'gray', go: () => toast({ msg: 'Mở: Báo giá Q26FRSGNNSA201' }) },
  ];
  const tabs = [
    { key: 'note' as const, label: 'Sổ tay' },
    { key: 'pipe' as const, label: 'Pipeline' },
    { key: 'noti' as const, label: 'Thông báo' },
  ];
  return (
    <DockPanel tabs={tabs} tab={dock.tab} onTab={dock.show} onClose={dock.close} closeLabel="Đóng cột phải">
      {dock.tab === 'note' && (
        <Sec title={t('crm.at.102')}>
          <div className={s.noteAdd}>
            <input aria-label="Ghi nhanh" placeholder={t('crm.at.103')} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={add}>
              {t('crm.x.add')}
            </Button>
          </div>
          {notes.map(([k, done], i) => (
            <div key={`${k}-${i}`} className={s.note1}>
              <input type="checkbox" aria-label={`Xong ${k}`} checked={done} onChange={() => setNotes(notes.map((x, j) => (j === i ? [x[0], !x[1]] : x)))} />
              <span style={{ textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--lb)' : 'var(--tx)' }}>{k}</span>
              <button type="button" aria-label={`Xoá ghi chú ${k}`} onClick={() => { setNotes(notes.filter((_, j) => j !== i)); toast({ msg: 'Đã xoá ghi chú' }); }}>
                ×
              </button>
            </div>
          ))}
          <div className={s.note}>{t('crm.dk.private')}</div>
        </Sec>
      )}
      {dock.tab === 'pipe' && (
        <Sec title="Pipeline theo sale">
          <div className={s.note} style={{ margin: '0 0 8px' }}>{t('crm.dk.stages')}</div>
          {PIPE.map(([k, v]) => (
            <div key={k} className={s.bar} style={{ gridTemplateColumns: '70px minmax(0,1fr) 70px', height: 30 }}>
              <span>{k}</span>
              <div role="img" aria-label={`${k}: Mới ${v[0]}, Chờ giá ${v[1]}, Đã báo ${v[2]}`} className={s.stack}>
                {v.map((n, i) => (
                  <div key={i} style={{ width: `${Math.round((n / 4) * 100)}%`, background: DOT[i] }} />
                ))}
              </div>
              <span style={{ textAlign: 'right', fontSize: 12 }}>{v.join(' / ')}</span>
            </div>
          ))}
        </Sec>
      )}
      {dock.tab === 'noti' && <Notis items={notis} />}
    </DockPanel>
  );
}
